import { ApplicationConfig, APP_INITIALIZER, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { authInterceptor } from './core/http/interceptors/auth.interceptor';
import { clientPlatformInterceptor } from './core/http/interceptors/client-platform.interceptor';
import { errorInterceptor } from './core/http/interceptors/error.interceptor';
import { AuthService } from './core/auth/auth.service';
import { AnalyticsService } from './core/services/analytics.service';
import { TranslationStore } from './core/services/translation.service';
import { DEFAULT_LANG, SUPPORTED_LANG_CODES } from './core/i18n/app-languages';

function initApp(auth: AuthService) {
  return () => auth.init();
}

/**
 * Language resolution lives in TranslationStore so the loaded translations, the
 * `currentLang` signal and `<html lang>` are set in one place. On the server it
 * loads (and awaits) the prerender language, so the generated static HTML contains
 * real copy rather than translation keys.
 */
function initTranslations(translate: TranslateService, store: TranslationStore) {
  return () => {
    translate.addLangs([...SUPPORTED_LANG_CODES]);
    translate.setDefaultLang(DEFAULT_LANG);
    return store.init();
  };
}

function initAnalytics(analytics: AnalyticsService) {
  return () => analytics.init();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions()),
    provideHttpClient(
      withFetch(),
      withInterceptors([errorInterceptor, authInterceptor, clientPlatformInterceptor]),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [TranslateService, TranslationStore],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAnalytics,
      deps: [AnalyticsService],
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot({ fallbackLang: 'en' }),
    ),
    provideTranslateHttpLoader({ prefix: 'assets/i18n/', suffix: '.json' }),
  ],
};
